import type {
  Command,
  CommandKey,
  CommandCategory,
  EventListener,
  CommandExecutionContext,
  SearchResult,
  SearchOptions,
  ExecutionResult,
  CommanderStats,
} from "./types";

import {
  CommandNotFoundError,
  CommandUnavailableError,
  CommandTimeoutError,
  CommandExecutionError,
  InputValidationError,
} from "./errors";

import {
  generateId,
  normalizeSearchTerm,
  calculateSearchScore,
  getMatchedTerms,
  isValidCommand,
  withTimeout,
  removeItem,
} from "./utils";

// ============================================================================
// COMMAND HANDLER CLASS
// ============================================================================

class CommandHandler {
  constructor(
    private key: CommandKey,
    private commander: Commander,
  ) {}

  exists(): boolean {
    return this.commander.has(this.key);
  }

  async isAvailable(): Promise<boolean> {
    const command = this.commander.getCommand(this.key);
    return command ? this.commander.isCommandAvailable(command) : false;
  }

  async invoke<T = any>(
    input?: any,
    source: "palette" | "shortcut" | "api" = "api",
  ): Promise<T> {
    return this.commander.invoke<T>(this.key, input, source);
  }

  async attempt<T = any>(
    input?: any,
    source: "palette" | "shortcut" | "api" = "api",
  ): Promise<ExecutionResult<T>> {
    return this.commander.attempt<T>(this.key, input, source);
  }

  getCommand(): Command | undefined {
    return this.commander.getCommand(this.key);
  }
}

// ============================================================================
// MAIN COMMANDER CLASS
// ============================================================================

export default class Commander {
  protected _commands: Map<CommandKey, Command> = new Map();
  protected listeners: Map<string, EventListener[]> = new Map();
  protected executionHistory: CommandExecutionContext[] = [];
  protected recentCommands: CommandKey[] = [];

  public maxHistorySize = 100;
  public maxRecentSize = 10;

  constructor(commands: Command[] = []) {
    commands.forEach((cmd) => this.add(cmd));
  }

  // ============================================================================
  // EVENT SYSTEM
  // ============================================================================

  listen<T = any>(
    event: string,
    callback: (...args: T[]) => void | Promise<void>,
    options: { once?: boolean } = {},
  ): EventListener<T> {
    const listener: EventListener<T> = {
      id: generateId(),
      event,
      callback,
      once: options.once,
    };

    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(listener as EventListener);

    return listener;
  }

  removeListener(listener: EventListener): void {
    for (const [event, listeners] of this.listeners) {
      const index = listeners.findIndex((l) => l.id === listener.id);
      if (index >= 0) {
        listeners.splice(index, 1);
        if (listeners.length === 0) {
          this.listeners.delete(event);
        }
        break;
      }
    }
  }

  private async emit(event: string, ...args: any[]): Promise<void> {
    const listeners = this.listeners.get(event) || [];
    const toRemove: EventListener[] = [];

    await Promise.allSettled(
      listeners.map(async (listener) => {
        try {
          await listener.callback(...args);
          if (listener.once) {
            toRemove.push(listener);
          }
        } catch (error) {
          console.error(`Event listener error for ${event}:`, error);
        }
      }),
    );

    // Remove one-time listeners
    toRemove.forEach((listener) => this.removeListener(listener));
  }

  // ============================================================================
  // COMMAND MANAGEMENT
  // ============================================================================

  commands(): Command[] {
    return Array.from(this._commands.values());
  }

  getCommand(key: CommandKey): Command | undefined {
    return this._commands.get(key);
  }

  add(command: Command): Command {
    if (!isValidCommand(command)) {
      throw new Error(`Invalid command: ${JSON.stringify(command)}`);
    }

    this._commands.set(command.key, command);
    this.emit("command:added", command);
    return command;
  }

  remove(commandOrKey: string | CommandKey | Command): boolean {
    const key =
      typeof commandOrKey === "string"
        ? (commandOrKey as CommandKey)
        : typeof commandOrKey === "object"
          ? commandOrKey.key
          : commandOrKey;

    const command = this._commands.get(key);
    if (command) {
      this._commands.delete(key);
      this.emit("command:removed", command);
      return true;
    }
    return false;
  }

  removeByOwner(owner: string): number {
    let removed = 0;
    for (const [key, command] of this._commands) {
      if (command.owner === owner) {
        this._commands.delete(key);
        this.emit("command:removed", command);
        removed++;
      }
    }
    return removed;
  }

  removeByCategory(category: CommandCategory): number {
    let removed = 0;
    for (const [key, command] of this._commands) {
      if (command.category === category) {
        this._commands.delete(key);
        this.emit("command:removed", command);
        removed++;
      }
    }
    return removed;
  }

  has(key: CommandKey): boolean {
    return this._commands.has(key);
  }

  // ============================================================================
  // SEARCH & FILTERING
  // ============================================================================

  async search(
    query: string,
    options: SearchOptions = {},
  ): Promise<SearchResult[]> {
    const normalizedQuery = normalizeSearchTerm(query);
    if (!normalizedQuery) {
      return this.getAllAvailable(options);
    }

    const results: SearchResult[] = [];
    const queryTerms = normalizedQuery.split(/\s+/);

    for (const command of this._commands.values()) {
      // Filter by options first
      if (options.category && command.category !== options.category) continue;
      if (options.owner && command.owner !== options.owner) continue;
      if (
        options.tags?.length &&
        !options.tags.some((tag) => command.tags?.includes(tag))
      )
        continue;

      // Check availability
      if (
        !options.includeUnavailable &&
        !(await this.isCommandAvailable(command))
      ) {
        continue;
      }

      const score = calculateSearchScore(command, queryTerms);
      if (score > 0) {
        results.push({
          command,
          score,
          matchedTerms: getMatchedTerms(command, queryTerms),
        });
      }
    }

    // Sort by score (desc) and priority (desc)
    results.sort((a, b) => {
      if (a.score !== b.score) return b.score - a.score;
      return (b.command.priority || 0) - (a.command.priority || 0);
    });

    return options.limit ? results.slice(0, options.limit) : results;
  }

  private async getAllAvailable(
    options: SearchOptions,
  ): Promise<SearchResult[]> {
    const results: SearchResult[] = [];

    for (const command of this._commands.values()) {
      if (options.category && command.category !== options.category) continue;
      if (options.owner && command.owner !== options.owner) continue;
      if (
        options.tags?.length &&
        !options.tags.some((tag) => command.tags?.includes(tag))
      )
        continue;

      if (
        !options.includeUnavailable &&
        !(await this.isCommandAvailable(command))
      ) {
        continue;
      }

      results.push({
        command,
        score: command.priority || 0,
        matchedTerms: [],
      });
    }

    // Sort by priority and recent usage
    results.sort((a, b) => {
      const aRecentIndex = this.recentCommands.indexOf(a.command.key);
      const bRecentIndex = this.recentCommands.indexOf(b.command.key);

      // Recent commands first
      if (aRecentIndex >= 0 && bRecentIndex >= 0) {
        return aRecentIndex - bRecentIndex;
      }
      if (aRecentIndex >= 0) return -1;
      if (bRecentIndex >= 0) return 1;

      // Then by priority
      return (b.command.priority || 0) - (a.command.priority || 0);
    });

    return options.limit ? results.slice(0, options.limit) : results;
  }

  // ============================================================================
  // COMMAND AVAILABILITY
  // ============================================================================

  async isCommandAvailable(command: Command): Promise<boolean> {
    if (!command.when) return true;

    try {
      return await command.when();
    } catch (error) {
      console.warn(
        `Command availability check failed for ${command.key}:`,
        error,
      );
      return false;
    }
  }

  async getAvailableCommands(
    options: {
      category?: CommandCategory;
      owner?: string;
    } = {},
  ): Promise<Command[]> {
    const available: Command[] = [];

    for (const command of this._commands.values()) {
      if (options.category && command.category !== options.category) continue;
      if (options.owner && command.owner !== options.owner) continue;

      if (await this.isCommandAvailable(command)) {
        available.push(command);
      }
    }

    return available;
  }

  // ============================================================================
  // EXECUTION
  // ============================================================================

  async invoke<T = any>(
    key: CommandKey,
    input?: any,
    source: "palette" | "shortcut" | "api" = "api",
  ): Promise<T> {
    const command = this._commands.get(key);
    if (!command) {
      const error = new CommandNotFoundError(key);
      this.emit("command:error", key, error);
      throw error;
    }

    // Check availability
    if (!(await this.isCommandAvailable(command))) {
      const error = new CommandUnavailableError(key);
      this.emit("command:error", key, error);
      throw error;
    }

    // Validate input if validator is defined
    if (command.inputValidator) {
      const validationResult = await command.inputValidator(input);
      if (validationResult !== true) {
        const error = new InputValidationError(key, validationResult, input);
        this.emit("command:validation-error", key, error);
        throw error;
      }
    }

    const context: CommandExecutionContext = {
      command,
      input,
      startTime: new Date(),
      source,
    };

    this.emit("command:executing", context);

    try {
      // Execute with timeout
      const result = await this.executeWithTimeout(command, input);

      // Track execution
      this.trackExecution(context, result);
      this.addToRecent(key);

      this.emit("command:completed", context, result);
      return result as T;
    } catch (error) {
      const executionError =
        error instanceof Error
          ? new CommandExecutionError(key, error)
          : new CommandExecutionError(key, new Error(String(error)));

      this.emit("command:failed", context, executionError);
      throw executionError;
    }
  }

  /**
   * Validate input against command's inputValidator without executing
   * Useful for pre-validation before invoking
   *
   * @returns true if valid, or ValidationErrorDetail[] if invalid
   */
  async validateInput<TInput = any>(
    key: CommandKey,
    input?: TInput,
  ): Promise<true | import("./types").ValidationErrorDetail[]> {
    const command = this._commands.get(key);
    if (!command) {
      throw new CommandNotFoundError(key);
    }

    if (!command.inputValidator) {
      return true;
    }

    return command.inputValidator(input);
  }

  private async executeWithTimeout<T>(
    command: Command,
    input?: any,
  ): Promise<T> {
    const timeoutMs = command.timeout || 30000;

    try {
      return await withTimeout(
        command.handle.call(this, input),
        timeoutMs,
        `Command timeout: ${command.key} (${timeoutMs}ms)`,
      );
    } catch (error) {
      if (error instanceof Error && error.message.includes("timeout")) {
        throw new CommandTimeoutError(command.key, timeoutMs);
      }
      throw error;
    }
  }

  async attempt<T = any>(
    key: CommandKey,
    input?: any,
    source: "palette" | "shortcut" | "api" = "api",
  ): Promise<ExecutionResult<T>> {
    try {
      const result = await this.invoke<T>(key, input, source);
      return { success: true, result, command: key };
    } catch (error) {
      return { success: false, error, command: key };
    }
  }

  // ============================================================================
  // EXECUTION TRACKING
  // ============================================================================

  private trackExecution(context: CommandExecutionContext, result: any): void {
    this.executionHistory.push({
      ...context,
      // Don't store large results in history
      input: typeof context.input === "object" ? "<object>" : context.input,
    });

    // Keep history size manageable
    if (this.executionHistory.length > this.maxHistorySize) {
      this.executionHistory.splice(
        0,
        this.executionHistory.length - this.maxHistorySize,
      );
    }
  }

  private addToRecent(key: CommandKey): void {
    // Remove if already exists
    const existingIndex = this.recentCommands.indexOf(key);
    if (existingIndex >= 0) {
      this.recentCommands.splice(existingIndex, 1);
    }

    // Add to front
    this.recentCommands.unshift(key);

    // Keep recent size manageable
    if (this.recentCommands.length > this.maxRecentSize) {
      this.recentCommands.splice(this.maxRecentSize);
    }
  }

  getExecutionHistory(limit?: number): CommandExecutionContext[] {
    return limit
      ? this.executionHistory.slice(-limit)
      : [...this.executionHistory];
  }

  getRecentCommands(): Command[] {
    return this.recentCommands
      .map((key) => this._commands.get(key))
      .filter((cmd): cmd is Command => cmd !== undefined);
  }

  clearHistory(): void {
    this.executionHistory = [];
    this.recentCommands = [];
    this.emit("history:cleared");
  }

  // ============================================================================
  // CATEGORIES & ORGANIZATION
  // ============================================================================

  getCommandsByCategory(): Map<CommandCategory, Command[]> {
    const categories = new Map<CommandCategory, Command[]>();

    for (const command of this._commands.values()) {
      const category = command.category || "custom";
      if (!categories.has(category)) {
        categories.set(category, []);
      }
      categories.get(category)!.push(command);
    }

    return categories;
  }

  getCategories(): CommandCategory[] {
    const categories = new Set<CommandCategory>();
    for (const command of this._commands.values()) {
      categories.add(command.category || "custom");
    }
    return Array.from(categories);
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  createInvoker(key: CommandKey): CommandHandler {
    return new CommandHandler(key, this);
  }

  // Alias for backward compatibility
  invoker(key: CommandKey): CommandHandler {
    return this.createInvoker(key);
  }

  getStats(): CommanderStats {
    return {
      totalCommands: this._commands.size,
      categories: this.getCategories().length,
      executionHistory: this.executionHistory.length,
      recentCommands: this.recentCommands.length,
      listeners: Array.from(this.listeners.values()).flat().length,
    };
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  destroy(): void {
    this._commands.clear();
    this.listeners.clear();
    this.executionHistory = [];
    this.recentCommands = [];
  }
}
